import React, { useState } from 'react';
import Modal from 'react-modal';
import moment from 'moment-timezone';
import cx from 'classnames';
import { DocumentNode } from 'graphql';
import { isPast, isToday } from 'date-fns';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Container from 'components/Container';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Grid from 'components/Grid';
import Icon from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';

import SaveSingleDayOverrideDialog from 'coaching/components/CoachAvailabilityView/SaveSingleDayOverrideDialog';
import {
  SingleDayCoachAvailability,
  coachAvailabilityViewEventPrefix,
} from 'coaching/containers/CoachAvailabilityContainer';
import { WeekTraversalHeader } from 'coaching/components/CoachingHomeView/BookOneOffSession';
import { userReadableDateFormat } from 'lib/dateTimeFormat';

import '../styles/CoachAvailabilitiesByDayList.scss';

export type Props = {
  availabilities: SingleDayCoachAvailability[];
  weekOffset: number;
  weekStartDate: string;
  weekEndDate: string;
  onWeekForward: () => void;
  onWeekBack: () => void;
  timezone: string;
  editable?: boolean;
  availabilityQuery?: { query: DocumentNode; variables?: Object };
};

export default function CoachAvailabilitiesByDayList({
  onWeekForward,
  onWeekBack,
  weekOffset,
  weekStartDate,
  weekEndDate,
  availabilities,
  timezone,
  editable = true,
  availabilityQuery,
}: Props) {
  const [singleDayAvailabilityToEdit, setSingleDayAvailabilityToEdit] = useState<
    SingleDayCoachAvailability | undefined
  >(undefined);

  function trackEvent(event: string) {
    EventTracking.trackKey(`${coachAvailabilityViewEventPrefix}.availabilities_by_day.${event}`);
  }

  return (
    <>
      <Modal isOpen={Boolean(singleDayAvailabilityToEdit)}>
        <SaveSingleDayOverrideDialog
          singleDayAvailability={singleDayAvailabilityToEdit}
          timezone={timezone}
          onRequestHide={() => setSingleDayAvailabilityToEdit(undefined)}
          availabilityQuery={availabilityQuery}
          eventPrefix={coachAvailabilityViewEventPrefix}
        />
      </Modal>
      <Container flush size="small" inline>
        <Box
          inline
          noShadow
          textAlign="left"
          className="CoachAvailabilitiesByDayList-box"
          bandHeader={
            <WeekTraversalHeader
              onWeekBack={() => {
                onWeekBack();
                trackEvent('previous_week.click');
              }}
              onWeekForward={() => {
                onWeekForward();
                trackEvent('next_week.click');
              }}
              startDateTimeRange={weekStartDate}
              endDateTimeRange={weekEndDate}
              weekOffset={weekOffset}
              timeRangeTextSize="small"
              size="xxxsmall"
              dateRangeTextColor="chalkboard"
              dateRangeTextWeight="semibold"
            />
          }
          bandHeaderSize="xsmall">
          <BoxBody size="xsmall">
            {availabilities.map((availability, index) => (
              <Row size="xxxxsmall" key={`${index}`}>
                <SingleDayCoachAvailabilityRow
                  availability={availability}
                  shouldShowDivider={index !== availabilities.length - 1}
                  onClick={row => {
                    setSingleDayAvailabilityToEdit(row);
                    trackEvent('edit_single_day_availability.click');
                  }}
                  editable={editable}
                />
              </Row>
            ))}
          </BoxBody>
        </Box>
      </Container>
    </>
  );
}

function SingleDayCoachAvailabilityRow(props: {
  availability: SingleDayCoachAvailability;
  onClick: (availability: SingleDayCoachAvailability) => void;
  shouldShowDivider?: boolean;
  editable: boolean;
}) {
  const { availability, shouldShowDivider, onClick, editable } = props;
  const { availableTimeframes } = availability;

  const date = availability.date;
  const isUnavailable = availableTimeframes.length === 0;
  const isTodayOrPast = isPast(date) || isToday(date);
  const textColor = isTodayOrPast ? 'graphite' : 'inherit';

  return (
    <Link
      color={textColor}
      onClick={() => onClick(availability)}
      href="javascript:;"
      disabled={!editable || isTodayOrPast}>
      <Grid
        columnGap="xxxxxsmall"
        columns={2}
        className={cx(
          'CoachAvailabilitiesByDayList-dayRow',
          !isTodayOrPast && 'CoachAvailabilitiesByDayList-dayRow-hoverable'
        )}
        tabIndex={isTodayOrPast ? -1 : 0}>
        <Text weight="semibold" lineHeight="large">
          {userReadableDateFormat(availability.date)}
        </Text>
        <MediaObject figure="right" center key={date}>
          <div
            className="CoachAvailabilitiesByDayList-dayRow-timeframes"
            key={`${availability.date}`}>
            {isUnavailable ? (
              <Text>Not available</Text>
            ) : (
              availableTimeframes.map(timeframe => {
                return (
                  <div key={`${availability.date}-${timeframe.startTime}`}>
                    <Text>
                      {moment(timeframe.startTime, 'HH:mm').format('h:mm A')}
                      {' - '}
                    </Text>
                    <Text>{moment(timeframe.endTime, 'HH:mm').format('h:mm A')}</Text>
                  </div>
                );
              })
            )}
            {availability.override && (
              <Text
                key={`${availability.date}-${availability.override.label}`}
                element="div"
                size="xxxsmall"
                color="graphite">
                Edited
              </Text>
            )}
          </div>
          <div>
            <Icon
              name="pencil-stroke"
              color="mascot"
              className="CoachAvailabilitiesByDayList-dayRow-editIcon"
            />
          </div>
        </MediaObject>
      </Grid>
      {shouldShowDivider && <Divider size="xxsmall" />}
    </Link>
  );
}
